import { getApp } from 'firebase/app'
import type { CollectionReference, DocumentReference, QueryConstraint } from 'firebase/firestore'
import { collection, doc, getFirestore, query } from 'firebase/firestore'
import { analysisSetConverter } from '~/firestore/converters/analysisSet'
import { discPresetConverter } from '~/firestore/converters/discs'
import {
    liveEventConverter,
    type LiveEvent,
    PlayEvent,
} from '~/firestore/converters/eventsConverter'
import { publicUserConverter } from '~/firestore/converters/publicUser'
import { simulatorCourseConverter } from '~/firestore/converters/simulatorCourse'
import { simulatorLayoutConverter } from '~/firestore/converters/simulatorLayout'
import { simulatorPermissionsConverter } from '~/firestore/converters/simulatorPermissions'
import { userAdminConverter } from '~/firestore/converters/userAdmin'
import { userContactsConverter } from '~/firestore/converters/userContactsConverter'
import { userRolesConverter } from '~/firestore/converters/userRolesConverter'
import type { UserContacts } from '~/hooks/useContacts/useContacts'
import type { AnalysisSet } from '~/types/analysisSets'
import type { Device, DeviceCalibration } from '~/types/device'
import type { DiscPreset } from '~/types/discs'
import type { LeaderboardUserMetadata } from '~/types/leaderboard'
import type {
    PromotedCourse,
    SimulatorChallenge,
    SimulatorCourse,
    SimulatorLayout,
    SimulatorPermission,
} from '~/types/simulator/simulator'
import type { ThrowSummary } from '~/types/throws'
import type { UserAdminInfo, UserRole } from '~/types/user'
import { deviceConverter } from './converters/device'
import { deviceCalibrationConverter } from './converters/deviceCalibration'
import { leaderboardUserMetadataConverter } from './converters/leaderboardUserMetadata'
import { throwSummaryConverter } from './converters/throwSummary'
import {
    ADMIN_COURSES_COLLECTION_PATH,
    ADMIN_LIVE_EVENTS_COLLECTION_PATH,
    ADMIN_PLAY_EVENTS_COLLECTION_PATH,
    ADMIN_PATH,
    ADMIN_SIMULATOR_COURSE_PATH,
    ADMIN_SIMULATOR_LAYOUT_PATH,
    ADMIN_SIMULATOR_LAYOUTS_COLLECTION_PATH,
    ADMIN_SIMULATOR_PERMISSIONS_COLLECTION_PATH,
    ADMIN_SIMULATOR_PERMISSIONS_PATH,
    ADMIN_USER_ROLES_COLLECTION_PATH,
    ANALYSIS_SET_COLLECTION_PATH,
    CONTACTS_COLLECTION_PATH,
    DEVICES_CALIBRATION_COLLECTION_PATH,
    DEVICES_COLLECTION_PATH,
    DISC_PRESETS_COLLECTION_PATH,
    LEADERBOARD_USER_METADATA_COLLECTION_PATH,
    PUBLIC_USER_PRESETS_COLLECTION_PATH,
    STORES_PRICING_PLANS_PATH,
    STRIPE_CHECKOUT_SESSION_COLLECTION_PATH,
    THROW_SUMMARY_COLLECTION_PATH,
    USER_ADMIN_COLLECTION_PATH,
    USER_DEVICES_COLLECTION_PATH,
    USER_PRICING_PLANS_PATH,
    USERS_PATH,
    LEADERBOARD_LAYOUTS_DOC,
    LEADERBOARD_PLAY_DOC,
    PROMOTED_COURSES_COLLECTION_PATH,
    ADMIN_SIMULATOR_CHALLENGES_COLLECTION_PATH,
} from './paths'
import { pricingPlanConverter, type PricingPlan } from './converters/pricingPlanConverter'
import { promotedCourseConverter } from './converters/promotedCourse'
import { simulatorChallengeConverter } from './converters/simulatorChallenge'

export function getUsersCollection(): CollectionReference {
    const firestore = getFirestore(getApp())
    return collection(firestore, USERS_PATH)
}

export function getUserThrowSummaries(uid: string): CollectionReference<ThrowSummary> {
    const firestore = getFirestore(getApp())
    return collection(firestore, THROW_SUMMARY_COLLECTION_PATH.replace('{uid}', uid)).withConverter(
        throwSummaryConverter,
    )
}

export function getFilteredThrowSummaries(uid: string, filters?: QueryConstraint[]) {
    const firestore = getFirestore(getApp())
    const summaries = collection(firestore, THROW_SUMMARY_COLLECTION_PATH.replace('{uid}', uid))
    return (filters ? query(summaries, ...filters) : summaries).withConverter(throwSummaryConverter)
}

export function getAdminUserRolesCollection(): CollectionReference<UserRole> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_USER_ROLES_COLLECTION_PATH).withConverter(userRolesConverter)
}

export function getAdminLiveEventsCollection(): CollectionReference<LiveEvent> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_LIVE_EVENTS_COLLECTION_PATH).withConverter(
        liveEventConverter,
    )
}

export function getAdminPlayEventsCollection(): CollectionReference<PlayEvent> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_PLAY_EVENTS_COLLECTION_PATH).withConverter(
        liveEventConverter,
    ) as CollectionReference<PlayEvent>
}

export function getAdminSimulatorCoursesCollection(): CollectionReference<SimulatorCourse> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_COURSES_COLLECTION_PATH).withConverter(
        simulatorCourseConverter,
    )
}

export function getPromotedCoursesCollection(): CollectionReference<PromotedCourse> {
    const firestore = getFirestore(getApp())
    return collection(firestore, PROMOTED_COURSES_COLLECTION_PATH).withConverter(
        promotedCourseConverter,
    )
}

export function getAdminSimulatorPermissionsCollection(): CollectionReference<SimulatorPermission> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_SIMULATOR_PERMISSIONS_COLLECTION_PATH).withConverter(
        simulatorPermissionsConverter,
    )
}

export function getPublicUserPresetsCollection(): CollectionReference {
    const firestore = getFirestore(getApp())
    return collection(firestore, PUBLIC_USER_PRESETS_COLLECTION_PATH).withConverter(
        publicUserConverter,
    )
}

export function getLeaderboardLayoutDoc(): DocumentReference {
    const firestore = getFirestore(getApp())
    return doc(firestore, LEADERBOARD_LAYOUTS_DOC)
}

export function getLeaderboardPlayDoc(): DocumentReference {
    const firestore = getFirestore(getApp())
    return doc(firestore, LEADERBOARD_PLAY_DOC)
}

export function getDevicesCollection(uid: string): CollectionReference<Device> {
    const firestore = getFirestore(getApp())
    return collection(firestore, USER_DEVICES_COLLECTION_PATH.replace('{uid}', uid)).withConverter(
        deviceConverter,
    )
}

export function getDevicesRootCollection(): CollectionReference<Device> {
    const firestore = getFirestore(getApp())
    return collection(firestore, DEVICES_COLLECTION_PATH).withConverter(deviceConverter)
}

export function getDevicesCalibrationCollection(
    uid: string,
    deviceId: string,
): CollectionReference<DeviceCalibration> {
    const firestore = getFirestore(getApp())
    return collection(
        firestore,
        DEVICES_CALIBRATION_COLLECTION_PATH.replace('{uid}', uid).replace('{deviceId}', deviceId),
    ).withConverter(deviceCalibrationConverter)
}

export function getStripeCheckoutSessionCollection(uid: string): CollectionReference | null {
    const firestore = getFirestore(getApp())
    return uid
        ? collection(firestore, STRIPE_CHECKOUT_SESSION_COLLECTION_PATH.replace('{uid}', uid))
        : null
}

export function getLeaderboardUserMetadataCollection(): CollectionReference<LeaderboardUserMetadata> {
    const firestore = getFirestore(getApp())
    return collection(firestore, LEADERBOARD_USER_METADATA_COLLECTION_PATH).withConverter(
        leaderboardUserMetadataConverter,
    )
}
export function getAnalysisSetCollection(uid: string): CollectionReference<AnalysisSet> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ANALYSIS_SET_COLLECTION_PATH.replace('{uid}', uid)).withConverter(
        analysisSetConverter,
    )
}

export function getUserAdminCollection(): CollectionReference<UserAdminInfo> {
    const firestore = getFirestore(getApp())
    return collection(firestore, USER_ADMIN_COLLECTION_PATH).withConverter(userAdminConverter)
}

export function getAdminSimulatorLayoutsCollection(): CollectionReference<SimulatorLayout> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_SIMULATOR_LAYOUTS_COLLECTION_PATH).withConverter(
        simulatorLayoutConverter,
    )
}

export function getAdminSimulatorChallengesCollection(): CollectionReference<SimulatorChallenge> {
    const firestore = getFirestore(getApp())
    return collection(firestore, ADMIN_SIMULATOR_CHALLENGES_COLLECTION_PATH).withConverter(
        simulatorChallengeConverter,
    )
}

export function getContactsCollection(): CollectionReference<UserContacts> {
    const firestore = getFirestore(getApp())
    return collection(firestore, CONTACTS_COLLECTION_PATH).withConverter(userContactsConverter)
}

export function getAdminSimulatorPermissions(): CollectionReference<SimulatorPermission> {
    return collection(getAdminCollection(), ADMIN_SIMULATOR_PERMISSIONS_PATH).withConverter(
        simulatorPermissionsConverter,
    )
}

export function getAdminSimulatorCourseCollection(): CollectionReference<SimulatorCourse> {
    return collection(getAdminCollection(), ADMIN_SIMULATOR_COURSE_PATH).withConverter(
        simulatorCourseConverter,
    )
}

export function getAdminSimulatorLayoutCollection(): CollectionReference<SimulatorLayout> {
    return collection(getAdminCollection(), ADMIN_SIMULATOR_LAYOUT_PATH).withConverter(
        simulatorLayoutConverter,
    )
}
export function getAdminCollection(): CollectionReference {
    return collection(getFirestore(getApp()), ADMIN_PATH)
}

export function getUserDiscPresets(uid: string): CollectionReference<DiscPreset> {
    return collection(
        getFirestore(getApp()),
        DISC_PRESETS_COLLECTION_PATH.replace('{uid}', uid),
    ).withConverter(discPresetConverter)
}

export function getUserPricingPlansCollection(): CollectionReference<PricingPlan> {
    return collection(getFirestore(getApp()), USER_PRICING_PLANS_PATH).withConverter(
        pricingPlanConverter,
    )
}

export function getStorePricingPlansCollection(): CollectionReference<PricingPlan> {
    return collection(getFirestore(getApp()), STORES_PRICING_PLANS_PATH).withConverter(
        pricingPlanConverter,
    )
}
